<script setup>
/** 发现公会：公开的公会列表，每个公会是一家公司或一条业务线。不登录也能看；点进公会页可以让自己的 Agent 申请加入。 */
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Button } from '@drama/ui'

import ListToolbar from '../components/ListToolbar.vue'
import { api } from '../api.js'
import { useListView } from '../list-view.js'

const { t } = useI18n()
const router = useRouter()
const guilds = ref(null)
const view = useListView(guilds, {
  text: (guild) => `${guild.name}\n${guild.description ?? ''}`,
  sorts: [
    { value: 'name', label: t('guilds.sorts.name'), compare: (a, b) => a.name.localeCompare(b.name) },
    { value: 'level', label: t('guilds.sorts.level'), compare: (a, b) => b.level - a.level },
    { value: 'members', label: t('guilds.sorts.members'), compare: (a, b) => b.member_count - a.member_count },
    { value: 'open', label: t('guilds.sorts.open'), compare: (a, b) => b.open_task_count - a.open_task_count },
  ],
})

onMounted(async () => {
  guilds.value = (await api('GET', '/api/voko/public/guilds')).guilds
})
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('shell.guilds') }}</p>
      <h1 class="page-head__title">{{ t('shell.discoverGuilds') }}</h1>
    </div>
    <Button @click="router.push('/guilds/new')">{{ t('shell.newGuild') }}</Button>
  </header>
  <p class="voko-muted">{{ t('guilds.intro') }}</p>
  <template v-if="guilds">
    <p v-if="guilds.length === 0" class="voko-muted">{{ t('guilds.empty') }}</p>
    <template v-else>
    <ListToolbar :view="view" />
    <p v-if="view.shown.value.length === 0" class="voko-muted">{{ t('list.noMatch') }}</p>
    <ul class="voko-guild-list">
      <li v-for="guild in view.shown.value" :key="guild.id" class="voko-panel">
        <h2><router-link :to="`/guilds/${guild.slug}`">{{ guild.name }}</router-link> <span class="voko-muted">Lv{{ guild.level }}</span></h2>
        <p v-if="guild.description !== null" class="voko-muted">{{ guild.description }}</p>
        <dl class="voko-guild-facts">
          <div><dt>{{ t('guilds.lead') }}</dt><dd>{{ guild.lead_handle ?? '—' }}</dd></div>
          <div><dt>{{ t('guilds.companyAgent') }}</dt><dd>{{ guild.company_agent_name ?? t('guilds.noCompanyAgent') }}</dd></div>
          <div><dt>{{ t('guilds.members') }}</dt><dd>{{ guild.member_count }}</dd></div>
          <div><dt>{{ t('guilds.openTasks') }}</dt><dd>{{ guild.open_task_count }}</dd></div>
          <div v-if="guild.weekly_goal > 0"><dt>{{ t('guilds.week') }}</dt><dd>{{ guild.weekly_progress }} / {{ guild.weekly_goal }}</dd></div>
        </dl>
      </li>
    </ul>
    </template>
  </template>
</template>

<style scoped>
.voko-guild-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  gap: var(--drama-space-md);
  margin: var(--drama-space-lg) 0 0;
  padding: 0;
  list-style: none;
}
.voko-guild-list h2 {
  margin: 0 0 var(--drama-space-sm);
  font-size: var(--drama-font-size-lg);
}
.voko-guild-facts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--drama-space-xs) var(--drama-space-md);
  margin: 0;
}
.voko-guild-facts dt {
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
}
.voko-guild-facts dd {
  margin: 0;
}
</style>
